import _extends from "/usr/app/node_modules/@babel/runtime/helpers/esm/extends.js";
var __jsx = React.createElement;
import CryptoJS from 'crypto-js';
import React from 'react';
import { SHOW_DEBUG_INFO } from "@globalConfig";

// Types

// Configuration
var SECRET_KEY = process.env.RAZZLE_LOACLSTORAGE_SECRET_KEY || 'default-secret-key';

// Utility to detect if data is encrypted
var isEncryptedData = function isEncryptedData(data) {
  try {
    var parsed = JSON.parse(data);
    return (parsed === null || parsed === void 0 ? void 0 : parsed.hasOwnProperty('encv1')) && (parsed === null || parsed === void 0 ? void 0 : parsed.data);
  } catch (_unused) {
    return false;
  }
};

// Storage operations
var storageFunctionCreator = function storageFunctionCreator(operation) {
  return function (key, value) {
    if (typeof window === 'undefined') return operation === 'get' ? null : undefined;
    try {
      switch (operation) {
        case 'get':
          {
            var item = localStorage.getItem(key);
            if (!item) return null;

            // Check if it's encrypted data
            if (isEncryptedData(item)) {
              try {
                var parsed = JSON.parse(item);
                var bytes = CryptoJS.AES.decrypt(parsed.data, SECRET_KEY);
                var decrypted = bytes.toString(CryptoJS.enc.Utf8);
                return JSON.parse(decrypted);
              } catch (error) {
                console.error('Decryption error:', error);
                return null;
              }
            }

            // Handle legacy non-encrypted data
            try {
              return item;
            } catch (_unused2) {
              return item;
            }
          }
        case 'set':
          {
            if (value === undefined) return;

            // Always encrypt new/updated values
            var encrypted = CryptoJS.AES.encrypt(JSON.stringify(value), SECRET_KEY).toString();
            localStorage.setItem(key, JSON.stringify({
              data: encrypted,
              encv1: true
            }));
            break;
          }
        case 'remove':
          {
            localStorage.removeItem(key);
            break;
          }
      }
    } catch (error) {
      console.error("Storage ".concat(operation, " error:"), error);
      return operation === 'get' ? null : undefined;
    }
  };
};

// HOC for class components
export var withStorage = function withStorage(WrappedComponent) {
  return function StorageComponent(props) {
    return /*#__PURE__*/React.createElement(WrappedComponent, _extends({}, props, {
      setLocalStorage: storageFunctionCreator('set'),
      getLocalStorage: storageFunctionCreator('get'),
      removeLocalStorage: storageFunctionCreator('remove'),
      clearLocalStorage: function clearLocalStorage() {
        if (typeof window === 'undefined') return;
        localStorage.clear();
      }
    }));
  };
};

// Modern React Hook
var useStorage = function useStorage() {
  var withEnc = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
  if (SHOW_DEBUG_INFO) withEnc = false;
  if (__IS_CSR__ && !withEnc) {
    return {
      setLocalStorage: function setLocalStorage(key, value) {
        window.localStorage.setItem(key, value);
      },
      getLocalStorage: function getLocalStorage(key) {
        return window.localStorage.getItem(key);
      },
      removeLocalStorage: function removeLocalStorage(key) {
        return window.localStorage.removeItem(key);
      },
      clearLocalStorage: function clearLocalStorage() {
        return window.localStorage.clear();
      }
    };
  }
  return {
    setLocalStorage: storageFunctionCreator('set'),
    getLocalStorage: storageFunctionCreator('get'),
    removeLocalStorage: storageFunctionCreator('remove'),
    clearLocalStorage: function clearLocalStorage() {
      if (typeof window === 'undefined') return;
      localStorage.clear();
    }
  };
};
export default useStorage;

/* Usage Examples:

// Class Component
class ExampleClass extends React.Component {
  componentDidMount() {
    const { setLocalStorage, getLocalStorage } = this.props;

    // Will automatically handle both old and new values
    const oldValue = getLocalStorage('existingKey'); // Works with legacy data

    // New value will be encrypted
    setLocalStorage('newKey', { some: 'data' });
  }
}
export default withStorage(ExampleClass);

// Functional Component
const ExampleFunction = () => {
  const { setLocalStorage, getLocalStorage } = useStorage();

  useEffect(() => {
    // Gets data regardless of encryption status
    const existingData = getLocalStorage('someKey');

    // Sets new data with encryption
    setLocalStorage('someKey', 'new value');
  }, []);
};
*/